// pages/Agents/AgentSpace/AgentSpaceMessageBubble.js
import React, { useState } from 'react';
import { Box, Avatar, Typography, IconButton, Collapse, Chip } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { marked } from 'marked';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#8b5cf6',
    success: '#059669',
    danger: '#dc2626',
    warning: '#f59e0b',
};

const AgentSpaceMessageBubble = React.memo(({ msg }) => {
    const isUser = msg.is_from_user || msg.role === 'USER';
    const [stepsExpanded, setStepsExpanded] = useState(true);
    const [copied, setCopied] = useState(false);

    const createMarkup = (text) => {
        try {
            const rawMarkup = marked.parse(text || '');
            return { __html: rawMarkup };
        } catch {
            return { __html: text || '' };
        }
    };

    const handleCopy = () => {
        if (msg.message) {
            navigator.clipboard.writeText(msg.message);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const steps = msg.steps || [];
    const hasSteps = steps.length > 0;
    const metadata = msg.metadata || {};

    if (isUser) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mb: 2.5 }}>
                <Box sx={{ maxWidth: '75%' }}>
                    {/* Agent type badge */}
                    {metadata.agent_type && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.6rem', color: colors.textTertiary }}>
                                {metadata.agent_type}
                                {metadata.selected_resources?.length > 0 && ` | ${metadata.selected_resources.length} resource(s)`}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{
                        bgcolor: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                        background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                        color: '#fff',
                        px: 2.5, py: 1.5,
                        borderRadius: '16px 16px 4px 16px',
                        boxShadow: '0 2px 8px rgba(139,92,246,0.2)',
                    }}>
                        <Typography sx={{
                            fontFamily, fontSize: '0.85rem', lineHeight: 1.6,
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        }}>
                            {msg.message}
                        </Typography>
                    </Box>
                </Box>
                <Avatar sx={{
                    width: 32, height: 32, bgcolor: '#e2e8f0',
                    flexShrink: 0, mt: metadata.agent_type ? 2 : 0,
                }}>
                    <PersonIcon sx={{ fontSize: 18, color: colors.textSecondary }} />
                </Avatar>
            </Box>
        );
    }

    // Agent message
    return (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
            <Avatar sx={{
                width: 32, height: 32, flexShrink: 0,
                background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
            }}>
                <AutoAwesomeIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Steps Display */}
                {hasSteps && (
                    <Box sx={{
                        bgcolor: 'rgba(139,92,246,0.04)',
                        border: '1px solid rgba(139,92,246,0.1)',
                        borderRadius: '12px',
                        mb: 1.5,
                        overflow: 'hidden',
                    }}>
                        {/* Steps Header */}
                        <Box
                            onClick={() => setStepsExpanded(!stepsExpanded)}
                            sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                px: 2, py: 1, cursor: 'pointer',
                                '&:hover': { bgcolor: 'rgba(139,92,246,0.06)' },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: colors.accent }}>
                                    {steps.length} action{steps.length !== 1 ? 's' : ''} executed
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {steps.filter(s => s.status === 'completed').length > 0 && (
                                        <Chip
                                            size="small"
                                            label={`${steps.filter(s => s.status === 'completed').length} done`}
                                            sx={{
                                                fontFamily, fontSize: '0.6rem', height: 18,
                                                bgcolor: 'rgba(5,150,105,0.1)', color: colors.success,
                                                fontWeight: 600,
                                            }}
                                        />
                                    )}
                                    {steps.filter(s => s.status === 'failed').length > 0 && (
                                        <Chip
                                            size="small"
                                            label={`${steps.filter(s => s.status === 'failed').length} failed`}
                                            sx={{
                                                fontFamily, fontSize: '0.6rem', height: 18,
                                                bgcolor: 'rgba(220,38,38,0.1)', color: colors.danger,
                                                fontWeight: 600,
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>
                            <IconButton size="small" sx={{ p: 0.25 }}>
                                {stepsExpanded ? <ExpandLessIcon sx={{ fontSize: 16, color: colors.textTertiary }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: colors.textTertiary }} />}
                            </IconButton>
                        </Box>

                        {/* Steps List */}
                        <Collapse in={stepsExpanded}>
                            <Box sx={{ px: 2, pb: 1.5 }}>
                                {steps.map((step, idx) => (
                                    <StepItem key={idx} step={step} index={idx} />
                                ))}
                            </Box>
                        </Collapse>
                    </Box>
                )}

                {/* Message Text */}
                {msg.message && (
                    <Box sx={{
                        bgcolor: 'rgba(255,255,255,0.85)',
                        border: '1px solid rgba(0,0,0,0.06)',
                        borderRadius: '16px 16px 16px 4px',
                        px: 2.5, py: 1.5,
                        position: 'relative',
                        '&:hover .msg-actions': { opacity: 1 },
                    }}>
                        <Box
                            sx={{
                                fontFamily, fontSize: '0.85rem', lineHeight: 1.7,
                                color: colors.textPrimary,
                                '& p': { margin: '0.5em 0' },
                                '& p:first-of-type': { marginTop: 0 },
                                '& p:last-of-type': { marginBottom: 0 },
                                '& ul, & ol': { pl: 2.5, my: 0.5 },
                                '& li': { mb: 0.25 },
                                '& code': {
                                    bgcolor: 'rgba(0,0,0,0.04)', px: 0.75, py: 0.25,
                                    borderRadius: '4px', fontSize: '0.8rem',
                                    fontFamily: "'Fira Code', monospace",
                                },
                                '& pre': {
                                    bgcolor: 'rgba(0,0,0,0.04)', p: 1.5,
                                    borderRadius: '8px', overflow: 'auto',
                                    '& code': { bgcolor: 'transparent', p: 0 },
                                },
                                '& strong': { fontWeight: 700 },
                                '& a': { color: colors.accent, textDecoration: 'none' },
                            }}
                            dangerouslySetInnerHTML={createMarkup(msg.message)}
                        />

                        {/* Copy button */}
                        <Box className="msg-actions" sx={{
                            position: 'absolute', top: 8, right: 8,
                            opacity: 0, transition: 'opacity 0.15s',
                        }}>
                            <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5 }}>
                                <ContentCopyIcon sx={{ fontSize: 14, color: copied ? colors.success : colors.textTertiary }} />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
});

function StepItem({ step, index }) {
    const [expanded, setExpanded] = useState(false);
    const isCompleted = step.status === 'completed';
    const isFailed = step.status === 'failed';
    const hasOutput = step.output && Object.keys(step.output).length > 0;

    return (
        <Box sx={{
            display: 'flex', gap: 1, py: 0.75,
            borderBottom: '1px solid rgba(0,0,0,0.04)',
            '&:last-child': { borderBottom: 'none' },
        }}>
            {/* Status Icon */}
            <Box sx={{ flexShrink: 0, pt: 0.25 }}>
                {isCompleted ? (
                    <CheckCircleIcon sx={{ fontSize: 16, color: colors.success }} />
                ) : isFailed ? (
                    <ErrorIcon sx={{ fontSize: 16, color: colors.danger }} />
                ) : (
                    <AccessTimeIcon sx={{ fontSize: 16, color: colors.textTertiary }} />
                )}
            </Box>

            {/* Step Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{
                        fontFamily, fontSize: '0.72rem', fontWeight: 500,
                        color: isFailed ? colors.danger : colors.textPrimary,
                    }}>
                        <Typography component="span" sx={{ fontFamily, fontSize: '0.65rem', color: colors.textTertiary, mr: 0.5 }}>
                            [{index + 1}]
                        </Typography>
                        {step.label || step.action_type}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        {step.duration_ms && (
                            <Typography sx={{ fontFamily, fontSize: '0.6rem', color: colors.textTertiary }}>
                                {step.duration_ms}ms
                            </Typography>
                        )}
                        {hasOutput && (
                            <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ p: 0.25 }}>
                                {expanded ? <ExpandLessIcon sx={{ fontSize: 14, color: colors.textTertiary }} /> : <ExpandMoreIcon sx={{ fontSize: 14, color: colors.textTertiary }} />}
                            </IconButton>
                        )}
                    </Box>
                </Box>

                {/* Error message */}
                {isFailed && step.output?.error && (
                    <Typography sx={{ fontFamily, fontSize: '0.65rem', color: colors.danger, mt: 0.25 }}>
                        {step.output.error}
                    </Typography>
                )}

                {/* Expanded output */}
                <Collapse in={expanded}>
                    {hasOutput && (
                        <Box sx={{
                            mt: 0.75, p: 1, bgcolor: 'rgba(0,0,0,0.03)',
                            borderRadius: '8px', maxHeight: 200, overflow: 'auto',
                            '&::-webkit-scrollbar': { width: '4px' },
                            '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: '4px' },
                        }}>
                            <Typography component="pre" sx={{
                                fontFamily: "'Fira Code', monospace",
                                fontSize: '0.6rem', color: colors.textSecondary,
                                whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0,
                            }}>
                                {JSON.stringify(step.output, null, 2)}
                            </Typography>
                        </Box>
                    )}
                </Collapse>
            </Box>
        </Box>
    );
}

AgentSpaceMessageBubble.displayName = 'AgentSpaceMessageBubble';

export default AgentSpaceMessageBubble;
